import { browser } from '$app/environment';

/**
 * Amazon store mappings based on country codes
 */
const AMAZON_STORES = {
    // North America
    US: 'amazon.com',
    CA: 'amazon.ca',
    MX: 'amazon.com.mx',

    // Europe
    GB: 'amazon.co.uk',
    IE: 'amazon.co.uk', // Ireland -> UK store
    DE: 'amazon.de',
    AT: 'amazon.de', // Austria -> German store
    CH: 'amazon.de', // Switzerland -> German store
    FR: 'amazon.fr',
    BE: 'amazon.fr', // Belgium -> French store
    LU: 'amazon.fr', // Luxembourg -> French store
    ES: 'amazon.es',
    PT: 'amazon.es', // Portugal -> Spanish store
    IT: 'amazon.it',
    NL: 'amazon.nl',
    // Removed PL and SE - book not available

    // Asia Pacific
    JP: 'amazon.co.jp',
    AU: 'amazon.com.au',
    IN: 'amazon.in',
    // Removed SG - book not available

    // Middle East - All removed, book not available
    // 'SA': 'amazon.sa', - 404
    // 'AE': 'amazon.ae', - 404
    // 'EG': 'amazon.eg', - 404

    // South America
    BR: 'amazon.com.br'
};

/**
 * Get Amazon store domain based on user's country
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {string} Amazon store domain
 */
export function getAmazonStore(countryCode) {
    if (!countryCode) return 'amazon.com';

    const upperCode = countryCode.toUpperCase();
    return AMAZON_STORES[upperCode] || 'amazon.com';
}

/**
 * Detect user's country using timezone and language detection only
 * @returns {string} Country code (ISO 3166-1 alpha-2)
 */
export function detectUserCountry() {
    if (!browser) return 'US';

    // Method 1: Try timezone-based detection first (fastest and most reliable)
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const countryFromTimezone = getCountryFromTimezone(timezone);
        if (countryFromTimezone) {
            return countryFromTimezone;
        }
    } catch (error) {
        console.debug('Timezone detection failed:', error);
    }

    // Method 2: Try browser language as fallback
    try {
        const language = navigator.language || navigator.languages?.[0];
        if (language) {
            const countryFromLang = getCountryFromLanguage(language);
            if (countryFromLang) {
                return countryFromLang;
            }
        }
    } catch (error) {
        console.debug('Language detection failed:', error);
    }

    // Default fallback
    return 'US';
}

/**
 * Extract country from timezone (best effort)
 * @param {string} timezone - IANA timezone identifier
 * @returns {string|null} Country code or null
 */
function getCountryFromTimezone(timezone) {
    const timezoneCountryMap = {
        // Major timezone mappings
        'America/New_York': 'US',
        'America/Los_Angeles': 'US',
        'America/Chicago': 'US',
        'America/Denver': 'US',
        'America/Toronto': 'CA',
        'America/Vancouver': 'CA',
        'Europe/London': 'GB',
        'Europe/Dublin': 'IE',
        'Europe/Berlin': 'DE',
        'Europe/Vienna': 'AT',
        'Europe/Zurich': 'CH',
        'Europe/Paris': 'FR',
        'Europe/Brussels': 'BE',
        'Europe/Madrid': 'ES',
        'Europe/Lisbon': 'PT',
        'Europe/Rome': 'IT',
        'Europe/Amsterdam': 'NL',
        // Removed Poland and Sweden timezones - book not available
        'Asia/Tokyo': 'JP',
        'Australia/Sydney': 'AU',
        'Australia/Melbourne': 'AU',
        // Removed Singapore timezone - book not available
        'Asia/Kolkata': 'IN'
        // Removed Saudi Arabia timezone - book not available
    };

    return timezoneCountryMap[timezone] || null;
}

/**
 * Extract country from language tag (best effort)
 * @param {string} language - Language tag (e.g., 'en-US', 'de-DE')
 * @returns {string|null} Country code or null
 */
function getCountryFromLanguage(language) {
    // Extract country code from language tag
    const parts = language.split('-');
    if (parts.length >= 2) {
        const countryCode = parts[1].toUpperCase();
        // Validate it's a supported Amazon country
        if (AMAZON_STORES[countryCode]) {
            return countryCode;
        }
    }

    // Map primary language to likely country
    const languageCountryMap = {
        en: 'US',
        de: 'DE',
        fr: 'FR',
        es: 'ES',
        it: 'IT',
        nl: 'NL',
        // Removed 'pl' and 'sv' - stores not available for this book
        ja: 'JP',
        pt: 'BR'
    };

    const primaryLang = parts[0].toLowerCase();
    return languageCountryMap[primaryLang] || null;
}

/**
 * Build Amazon URL for a book using ASIN
 * @param {string} asin - Amazon Standard Identification Number
 * @param {string} countryCode - Country code
 * @param {string} affiliateTag - Optional affiliate tag
 * @returns {string} Amazon URL for the book
 */
export function getAmazonBookUrl(
    asin,
    countryCode = null,
    affiliateTag = null
) {
    const store = getAmazonStore(countryCode || detectUserCountry());
    let url = `https://${store}/dp/${asin}`;

    if (affiliateTag) {
        url += `?tag=${affiliateTag}`;
    }

    return url;
}
